// Row contracts of the agency console's control-plane tables (M5-05; 02 `break_glass_session`,
// `portfolio_projection`, `membership_counts`). Same conventions as `rows.ts`: keys are column
// names, nullability is the column's, enums are the CHECK's; bound in `contract-bindings.ts`.
import { z } from "zod";
import {
  BreakGlassReadSubjectKind,
  BreakGlassScope,
  BreakGlassState,
  PortfolioProjectionKind,
} from "./enums";
import { Timestamp, Uuid } from "./primitives";

/** Who decided a break-glass request: a second agency admin, or the company's owner (05). */
export const BreakGlassDecider = z.enum(["agency_admin", "company_owner"]);

export const BreakGlassSession = z
  .object({
    id: Uuid,
    agency_id: Uuid,
    company_id: Uuid,
    requested_by_user_id: Uuid,
    requested_by_name: z.string(),
    reason: z.string(),
    scope: BreakGlassScope,
    scope_id: Uuid.nullable(),
    duration_minutes: z.int().min(5).max(240),
    state: BreakGlassState,
    requested_at: Timestamp,
    decided_by_kind: BreakGlassDecider.nullable(),
    decided_by_user_id: Uuid.nullable(),
    decided_by_name: z.string().nullable(),
    decided_at: Timestamp.nullable(),
    reject_reason: z.string().nullable(),
    starts_at: Timestamp.nullable(),
    expires_at: Timestamp.nullable(),
    ended_at: Timestamp.nullable(),
    ended_by_user_id: Uuid.nullable(),
    ended_by_name: z.string().nullable(),
    end_reason: z.string().nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "BreakGlassSession" });
export type BreakGlassSession = z.infer<typeof BreakGlassSession>;

/** BREAK-GLASS: one resource an agency user read under a live session (`cell.break_glass_read`). */
export const BreakGlassRead = z
  .object({
    company_id: Uuid,
    id: Uuid,
    session_id: Uuid,
    agency_user_id: Uuid,
    agency_user_name: z.string(),
    subject_kind: BreakGlassReadSubjectKind,
    subject_id: Uuid,
    route: z.string(),
    minute: Timestamp,
    read_at: Timestamp,
  })
  .meta({ id: "BreakGlassRead" });
export type BreakGlassRead = z.infer<typeof BreakGlassRead>;

export const PortfolioProjection = z
  .object({
    id: Uuid,
    account_id: Uuid.nullable(),
    company_id: Uuid,
    period_key: z.string(),
    kind: PortfolioProjectionKind,
    snapshot: z.record(z.string(), z.unknown()),
    version: z.int().min(1),
    source_event_id: Uuid,
    source_seq: z.int().min(1),
    published_at: Timestamp,
    created_at: Timestamp,
  })
  .meta({ id: "PortfolioProjection" });
export type PortfolioProjection = z.infer<typeof PortfolioProjection>;

export const MembershipCounts = z
  .object({
    user_id: Uuid,
    company_id: Uuid,
    unread: z.int().min(0),
    approvals: z.int().min(0),
    source_seq: z.int().min(1),
    updated_at: Timestamp,
  })
  .meta({ id: "MembershipCounts" });
export type MembershipCounts = z.infer<typeof MembershipCounts>;
